import React, { useState, InputHTMLAttributes } from "react";
import { useIntl } from "react-intl";
import * as Form from "@radix-ui/react-form";
import {
  CircleCheck,
  Mail,
  Plus,
  CircleX,
  CircleAlert,
  Info,
} from "@labxchange/icons";

import { RichText } from "@learningblocks/shared-components/lib/RichText";
import { Tooltip } from "@labxchange/ui-components/lib/Tooltip";

import {
  containerClass,
  labelClass,
  inputWrapperClass,
  inputWrapperBorderlessClass,
  inputClass,
  inputBorderlessClass,
  iconClass,
  helpTextClass,
  errorTextClass,
  feedbackIconClass,
  hintIconClass,
  iconSpacerClass,
} from "./TextInput.css";

export type TextInputIconType =
  | "check"
  | "message"
  | "plus"
  | "cross"
  | "warning";

const ICON_MAP = {
  check: CircleCheck,
  message: Mail,
  plus: Plus,
  cross: CircleX,
  warning: CircleAlert,
} as const;

export interface ValidationMessage {
  match: Form.FormMessageProps["match"];
  message: string | { id: string; defaultMessage: string };
}

/**
 * LxTextInput - A text input component built on Radix UI Form primitives.
 *
 * IMPORTANT: This component requires a `<Form.Root>` ancestor in the component tree.
 * The Form.Root provides the form context needed for validation and accessibility features.
 *
 * @example
 * ```tsx
 * import * as Form from "@radix-ui/react-form";
 *
 * <Form.Root>
 *   <LxTextInput name="email" label="Email" />
 *   <Form.Submit>Submit</Form.Submit>
 * </Form.Root>
 * ```
 */

export type TextInputVariant = "default" | "borderless";

export interface LxTextInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "prefix" | "name" | "placeholder"
  > {
  label?: string | { id: string; defaultMessage: string };
  helpText?: string | { id: string; defaultMessage: string };
  errorText?: string | { id: string; defaultMessage: string };
  placeholder?: string | { id: string; defaultMessage: string };
  prefix?: TextInputIconType;
  suffix?: TextInputIconType;
  state?: "default" | "success" | "error";
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  type?: string;
  id?: string;
  name: string;
  validationMessages?: ValidationMessage[];
  validateOnChange?: boolean;
  variant?: TextInputVariant;
  /** Feedback text to show in tooltip when hovering over feedback icon */
  feedback?: string;
  /** Whether to show the feedback icon with tooltip */
  showFeedback?: boolean;
  /** Hint text to show in tooltip when hovering over hint icon (shown when no value) */
  hint?: string;
  /** Size of the hint icon (default: 18, use 24 for table cells) */
  hintIconSize?: number;
}

export const LxTextInput = React.forwardRef<HTMLInputElement, LxTextInputProps>(
  (
    {
      label,
      helpText,
      errorText,
      prefix,
      suffix,
      state = "default",
      value,
      onChange,
      disabled = false,
      placeholder,
      type = "text",
      id,
      name,
      validationMessages,
      validateOnChange = true,
      variant = "default",
      feedback,
      showFeedback = false,
      hint,
      hintIconSize = 18,
      ...rest
    },
    ref
  ) => {
    const intl = useIntl();
    const [isFocused, setIsFocused] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [internalValue, setInternalValue] = useState(value || "");

    const generatedId = React.useId();
    const inputId = id || generatedId;
    const descriptionId = `${inputId}-description`;

    const currentValue = value !== undefined ? value : internalValue;
    const hasValue = currentValue.length > 0;
    const showError = state === "error" && errorText;
    const displayHelpText = showError ? errorText : helpText;

    const translatedPlaceholder =
      typeof placeholder === "object"
        ? intl.formatMessage(placeholder)
        : placeholder;

    const getDataState = () => {
      if (disabled) return "disabled";
      if (state === "error") return "error";
      if (state === "success") return "success";
      if (isFocused) return "typing";
      return "default";
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(event.target.value);
      onChange?.(event);
    };

    const handleBlur = () => {
      setIsFocused(false);
      setIsDirty(true);
    };

    const PrefixIcon = prefix ? ICON_MAP[prefix] : null;
    const SuffixIcon = suffix ? ICON_MAP[suffix] : null;
    const dataState = getDataState();

    return (
      <Form.Field
        name={name}
        serverInvalid={state === "error"}
        className={containerClass}
      >
        {label && (
          <Form.Label className={labelClass} data-disabled={disabled}>
            <RichText value={label} />
          </Form.Label>
        )}
        <div
          className={
            variant === "borderless"
              ? inputWrapperBorderlessClass
              : inputWrapperClass
          }
          data-state={dataState}
          data-disabled={disabled}
          data-has-value={hasValue}
        >
          {PrefixIcon && (
            <div className={iconClass} aria-hidden="true">
              <PrefixIcon size={24} />
            </div>
          )}
          <Form.Control asChild>
            <input
              ref={ref}
              id={inputId}
              type={type}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              placeholder={translatedPlaceholder}
              className={
                variant === "borderless" ? inputBorderlessClass : inputClass
              }
              onFocus={() => setIsFocused(true)}
              onBlur={handleBlur}
              data-state={dataState}
              aria-invalid={state === "error" || undefined}
              aria-describedby={displayHelpText ? descriptionId : undefined}
              {...rest}
            />
          </Form.Control>
          {variant === "borderless" ? (
            // In borderless mode, always reserve space to prevent layout shift
            hint && !hasValue ? (
              <Tooltip content={hint} variant="dark" placement="bottom">
                <span className={hintIconClass}>
                  <Info size={hintIconSize} />
                </span>
              </Tooltip>
            ) : SuffixIcon && showFeedback && feedback ? (
              <Tooltip
                content={feedback}
                variant={state === "success" ? "success" : "fail"}
                placement="bottom"
                showIcon
              >
                <span className={feedbackIconClass}>
                  <SuffixIcon size={24} />
                </span>
              </Tooltip>
            ) : (
              <div className={iconSpacerClass} aria-hidden="true" />
            )
          ) : (
            <>
              {SuffixIcon &&
                (showFeedback && feedback ? (
                  <Tooltip
                    content={feedback}
                    variant={state === "success" ? "success" : "fail"}
                    placement="bottom"
                    showIcon
                  >
                    <span className={feedbackIconClass}>
                      <SuffixIcon size={24} />
                    </span>
                  </Tooltip>
                ) : (
                  <div className={iconClass} aria-hidden="true">
                    <SuffixIcon size={24} />
                  </div>
                ))}
              {hint && !hasValue && (
                <Tooltip content={hint} variant="dark" placement="bottom">
                  <span className={hintIconClass}>
                    <Info size={hintIconSize} />
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </div>
        {isDirty &&
          validationMessages?.map((validation, index) => (
            <Form.Message
              key={index}
              match={validation.match}
              className={errorTextClass}
              forceMatch={validateOnChange}
            >
              {typeof validation.message === "object"
                ? intl.formatMessage(validation.message)
                : validation.message}
            </Form.Message>
          ))}
        {displayHelpText && (
          <p
            id={descriptionId}
            className={showError ? errorTextClass : helpTextClass}
            data-disabled={disabled}
            role={showError ? "alert" : undefined}
          >
            <RichText value={displayHelpText} />
          </p>
        )}
      </Form.Field>
    );
  }
);

LxTextInput.displayName = "LxTextInput";
